import { Card, CardList } from '@blueprintjs/core';
import EditableDetailPanel from 'Components/Generic/DetailPanel/EditableDetailPanel';
import { user_roles } from 'Data/Enum';
import GenericTableData from 'Data/GenericTableData';
import DataManager from 'Lib/DataManager';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { isUserAuthorized } from 'Util';
import AddNewButton from '../Buttons/AddNewButton';
import ButtonFooter from '../Buttons/ButtonFooter';
import UndoButton from '../Buttons/UndoButton';
import NoContentBox from '../Misc/NoContentBox';
import AddressDetailTable from './AddressDetailTable';

let DM = new DataManager();
let AddressData = GenericTableData.getTableDataBus('ADD', 'addresses');

const AddressListPanel = ({
  addressIdList,
  deleteAddressLinks,
  editable = false,
  canAddAddress = false,
  addressType,
  onSaveAddress = () => {},
  ...props
}) => {
  const [addressList, setAddressList] = useState([]);
  const [hasBeenUpdated, setHasBeenUpdated] = useState(false);
  const [editState, setEditState] = useState();

  useEffect(() => {
    DM.add(
      AddressData.registerListener('BLANK_RECORD_LOADED', onBlankAddressLoaded),
      AddressData,
    );
    DM.add(
      AddressData.registerListener('RECORDS_LOADED', onAddressListLoaded),
      AddressData,
    );
    DM.add(
      AddressData.registerListener(
        'RECORDS_LOAD_ERROR',
        onAddressListLoadError,
      ),
      AddressData,
    );
    return () => {
      DM.clear();
    };
  }, []);

  useEffect(() => {
    fetchAddresses();
  }, [addressIdList]);

  useEffect(() => {
    setEditState(false);
  }, [addressList]);

  const fetchAddresses = () => {
    if (addressIdList?.length) {
      AddressData.getRecords(addressIdList, true);
    } else {
      setAddressList([]);
    }
  };

  const onAddressListLoaded = (payload) => {
    setHasBeenUpdated(false);
    setAddressList(payload);
  };

  const onAddressListLoadError = () => {};

  const onBlankAddressLoaded = (newAddress) => {
    setHasBeenUpdated(true);
    setAddressList((prevAddresses) => [...prevAddresses, newAddress]);
  };

  const onAddressSaved = (isNew, updatedAddress) => {
    onSaveAddress(isNew, updatedAddress);
    setEditState(undefined);
  };

  const onDeleteItem = (addressToDelete) => {
    setEditState(undefined);
    setAddressList((prevList) =>
      [...prevList].filter((item) => item !== addressToDelete),
    );
  };

  const undoChanges = () => {
    setAddressList([]);
    fetchAddresses();
    setHasBeenUpdated(false);
  };

  const newButton = canAddAddress && (
    <AddNewButton
      clickHandler={AddressData.getBlank}
      buttonText='Add Address'
    />
  );

  const undoButton = hasBeenUpdated && (
    <UndoButton clickHandler={undoChanges} />
  );

  const footer = (
    <ButtonFooter>
      {undoButton}
      {newButton}
    </ButtonFooter>
  );

  const getAddressTables = (inEditState) => {
    return addressList.map((address, index) => {
      return (
        <Card
          key={`${address.id || index}-${address.createdAt}`}
          interactive={inEditState}>
          <AddressDetailTable
            deleteAddressLinks={deleteAddressLinks}
            addressObject={address}
            editable={inEditState}
            onUpdateAddress={(isUpdated) =>
              setHasBeenUpdated((prevValue) => prevValue || isUpdated)
            }
            onSaveAddress={onAddressSaved}
            onDeleteAddress={() => onDeleteItem(address)}
            addressType={addressType}
          />
        </Card>
      );
    });
  };

  const getAddressListContent = (inEditState) => {
    return !addressList || addressList.length === 0 ? (
      <NoContentBox label='No Addresses' />
    ) : (
      <CardList bordered>{getAddressTables(inEditState)}</CardList>
    );
  };

  return (
    <EditableDetailPanel
      title='Addresses'
      editable={
        isUserAuthorized({
          roles: [user_roles.ADMINISTRATOR],
        }) &&
        (editable || canAddAddress)
      }
      parentEditState={editState}
      renderChild={getAddressListContent}
      footer={footer}
      collapsible
      {...props}
    />
  );
};

AddressListPanel.propTypes = {
  addressIdList: PropTypes.array.isRequired,
  deleteAddressLinks: PropTypes.func.isRequired,
  editable: PropTypes.bool,
  canAddAddress: PropTypes.bool,
  addressType: PropTypes.string,
  onSaveAddress: PropTypes.func,
};

export default AddressListPanel;
